import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import './LoginModal.css';

interface RegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToLogin: () => void;
  onSuccess?: () => void;
}

export const RegisterModal: React.FC<RegisterModalProps> = ({
  isOpen,
  onClose,
  onSwitchToLogin,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { register, error, clearError, isLoading } = useAuth();
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');
    clearError();

    // Validation
    if (!name.trim()) {
      setValidationError(t('auth.errors.nameRequired'));
      return;
    }
    if (name.trim().length < 2) {
      setValidationError(t('auth.errors.nameTooShort'));
      return;
    }
    if (!email.trim()) {
      setValidationError(t('auth.errors.emailRequired'));
      return;
    }
    if (!email.includes('@')) {
      setValidationError(t('auth.errors.invalidEmail'));
      return;
    }
    if (!password) {
      setValidationError(t('auth.errors.passwordRequired'));
      return;
    }
    if (password.length < 3) {
      setValidationError(t('auth.errors.passwordTooShort'));
      return;
    }
    if (password !== confirmPassword) {
      setValidationError(t('auth.errors.passwordMismatch'));
      return;
    }

    // Attempt registration
    const success = await register(name.trim(), email.trim(), password);
    
    if (success) {
      // Reset form
      setName('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setValidationError('');
      
      // Close modal and call success callback
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    }
  };

  const handleClose = () => {
    setName('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setValidationError('');
    clearError();
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleSwitchToLogin = () => {
    setName('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setValidationError('');
    clearError();
    onSwitchToLogin();
  };

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div className="modal-content auth-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t('auth.register')}</h3>
          <button className="modal-close" onClick={handleClose} disabled={isLoading}>
            ×
          </button>
        </div>

        <form className="modal-body auth-form" onSubmit={handleSubmit}>
          {(validationError || error) && (
            <div className="auth-error">
              {validationError || error}
            </div>
          )}

          <div className="auth-field">
            <label htmlFor="register-name">{t('auth.name')}</label>
            <input
              id="register-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('auth.namePlaceholder')}
              disabled={isLoading}
              autoFocus
            />
          </div>

          <div className="auth-field">
            <label htmlFor="register-email">{t('auth.email')}</label>
            <input
              id="register-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.emailPlaceholder')}
              disabled={isLoading}
            />
          </div>

          <div className="auth-field">
            <label htmlFor="register-password">{t('auth.password')}</label>
            <input
              id="register-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('auth.passwordPlaceholder')}
              disabled={isLoading}
            />
          </div>

          <div className="auth-field">
            <label htmlFor="register-confirm-password">{t('auth.confirmPassword')}</label>
            <input
              id="register-confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('auth.confirmPasswordPlaceholder')}
              disabled={isLoading}
            />
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClose}
              disabled={isLoading}
            >
              {t('auth.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading}
            >
              {isLoading ? t('auth.registering') : t('auth.registerButton')}
            </button>
          </div>

          <div className="auth-switch">
            {t('auth.alreadyHaveAccount')}{' '}
            <button
              type="button"
              className="auth-switch-link"
              onClick={handleSwitchToLogin}
              disabled={isLoading}
            >
              {t('auth.loginLink')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
